import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Loader2, AlertTriangle, ExternalLink } from "lucide-react";

/**
 * /q/:code — Public short-URL resolver.
 *
 * Hits /api/qr/resolve/:code (which logs the scan) and immediately redirects
 * to the destination. When the owner's subscription has lapsed we show a
 * branded "temporarily inactive" screen instead of leaking them anywhere.
 */
export default function QRRedirect() {
  const { code } = useParams();
  const [state, setState] = useState({ status: "loading", destination: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/qr/resolve/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (data?.status === "ok" && data?.destination) {
          // Small delay to avoid tab-freeze feel on very fast networks
          window.location.replace(data.destination);
        } else {
          setState(data || { status: "not_found" });
        }
      } catch (e) {
        if (!cancelled) setState({ status: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  return (
    <div className="relative min-h-screen bg-[#0a0812] text-white flex items-center justify-center px-4 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 12% 18%, rgba(88,28,135,0.45), transparent 60%)," +
            "radial-gradient(50% 55% at 85% 78%, rgba(0,229,255,0.16), transparent 65%)," +
            "linear-gradient(180deg, #0a0812 0%, #0e0a1c 50%, #050408 100%)",
        }}
      />
      <div className="relative max-w-md w-full text-center">
        <div className="mb-6 flex justify-center">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #F2C94C 0%, #E27D60 50%, #7c3aed 100%)",
              boxShadow: "0 0 24px -4px rgba(242,201,76,0.55)",
            }}
          >
            <Sparkles className="w-6 h-6 text-[#0A0A0A]" />
          </div>
        </div>

        {state.status === "loading" && (
          <div data-testid="qr-redirect-loading">
            <Loader2 className="w-6 h-6 text-[#F2C94C] animate-spin mx-auto mb-4" />
            <div className="font-display text-2xl mb-2">Redirecting…</div>
            <p className="font-body text-sm text-white/70">
              You're being taken to the destination this QR code points to.
            </p>
          </div>
        )}

        {state.status === "inactive" && (
          <div data-testid="qr-redirect-inactive">
            <AlertTriangle className="w-6 h-6 text-[#F2C94C] mx-auto mb-4" />
            <div className="font-display text-3xl mb-3">
              This QR is <span className="text-[#F2C94C]">temporarily inactive</span>
            </div>
            <p className="font-body text-sm text-white/80 leading-relaxed mb-6">
              The owner's dynamic QR subscription has lapsed. They'll reactivate it shortly — scan again in a bit.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-[#00E5FF]/40 hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] font-mono-hy text-[10px] tracking-[0.22em] text-[#00E5FF] transition-all"
            >
              GO TO GENEO AI
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {state.status === "not_found" && (
          <div data-testid="qr-redirect-notfound">
            <AlertTriangle className="w-6 h-6 text-[#EB5757] mx-auto mb-4" />
            <div className="font-display text-3xl mb-3">QR code not found</div>
            <p className="font-body text-sm text-white/70 leading-relaxed mb-6">
              This short link doesn't match any Geneo AI QR code.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold transition-all"
            >
              GO TO GENEO AI
            </Link>
          </div>
        )}

        {state.status === "disabled" && (
          <div data-testid="qr-redirect-disabled">
            <div className="font-display text-3xl mb-3">This QR is paused</div>
            <p className="font-body text-sm text-white/70">The owner has temporarily disabled this code.</p>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="qr-redirect-error">
            <AlertTriangle className="w-6 h-6 text-[#EB5757] mx-auto mb-4" />
            <div className="font-display text-2xl mb-2">Something went wrong</div>
            <p className="font-body text-sm text-white/70">Please try scanning again in a moment.</p>
          </div>
        )}

        <div className="mt-10 font-mono-hy text-[9px] text-white/60 tracking-[0.2em]">
          MADE WITH · GENEO AI · AFRO SCI-FI
        </div>
      </div>
    </div>
  );
}
